import { useEffect } from "react"
import { Modal, Form, Input, Select, DatePicker } from "antd"
import {
  TrainingRecordModalProps,
  TrainingRecordFormData,
} from "../types"
import dayjs, { Dayjs } from "dayjs"
import { ID } from "@/types/common"

const { TextArea } = Input
const { Option } = Select

// 内部表单数据类型，用于处理Dayjs对象
interface InternalFormData {
  trainingPlanId?: ID
  assistTypeId?: ID
  trainingDate?: Dayjs
  userId?: ID
  note?: string
}

const TrainingRecordModal = ({
  isOpen,
  onClose,
  onSave,
  trainingRecord,
  loading = false,
  trainingPlans,
  trainingPlansLoading,
  assistTypes,
  assistTypesLoading,
  users,
  usersLoading,
  initialValues,
}: TrainingRecordModalProps) => {
  const [form] = Form.useForm<InternalFormData>()

  // 编辑模式：填充现有数据
  useEffect(() => {
    if (!isOpen) return
    form.setFieldsValue({
      trainingPlanId: initialValues.trainingPlanId,
    })
    if (trainingRecord) {
      // 编辑模式
      form.setFieldsValue({
        trainingPlanId: trainingRecord.trainingPlanId,
        assistTypeId: trainingRecord.assistTypeId,
        trainingDate: trainingRecord.trainingDate
          ? dayjs(trainingRecord.trainingDate)
          : undefined,
        userId: trainingRecord.userId,
        note: trainingRecord.note,
      })
    } else {
      // 新增模式，重置表单
      form.resetFields()
      form.setFieldsValue({
        trainingPlanId: initialValues.trainingPlanId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, trainingRecord])

  // 转换内部表单数据为API格式
  const convertToTrainingRecordFormat = (
    data: InternalFormData
  ): TrainingRecordFormData => ({
    trainingPlanId: data.trainingPlanId!,
    assistTypeId: data.assistTypeId,
    trainingDate: data.trainingDate?.format("YYYY-MM-DD") || "",
    userId: data.userId!,
    note: data.note?.trim(),
  })

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData = convertToTrainingRecordFormat(values)
    await onSave(formData)
  }

  // 处理取消
  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={trainingRecord ? "编辑训练记录" : "新增训练记录"}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={700}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="训练计划"
            name="trainingPlanId"
            rules={[{ required: true, message: "请选择训练计划" }]}
          >
            <Select
              placeholder="请选择训练计划"
              loading={trainingPlansLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {trainingPlans.map((plan) => (
                <Option key={plan.id} value={plan.id}>
                  {plan.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="辅助类型"
            name="assistTypeId"
          >
            <Select
              placeholder="请选择辅助类型"
              loading={assistTypesLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {assistTypes.map((assistType) => (
                <Option key={assistType.id} value={assistType.id}>
                  {assistType.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="训练日期"
            name="trainingDate"
            rules={[{ required: true, message: "请选择训练日期" }]}
          >
            <DatePicker
              placeholder="请选择训练日期"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item
            label="教师"
            name="userId"
            rules={[{ required: true, message: "请选择记录人" }]}
          >
            <Select
              placeholder="请选择记录人"
              loading={usersLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item label="备注" name="note">
          <TextArea
            placeholder="请输入备注（可选）"
            rows={4}
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TrainingRecordModal
